import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Select } from '@/components/Select'
import {
  api,
  type AudienceMember,
  type ProjectResponse,
  type ReachCampaignResponse,
  type ReachAddressabilitySummary,
  type ReachAddressabilitySummaryResponse,
  type ReachConversationResponse,
  type ReachConversationStatus,
  type ReachConversationSummary,
  type ReachSandboxDryRunResponse,
  type ReachSandboxSendResponse,
} from '@/lib/api'
import { buildProjectNumberMap, projectLabelById } from '@/lib/projectLabels'
import { useAuth } from '@/lib/auth'
import { UpsellNotice } from '@/components/UpsellNotice'

type VerdictFilter = AudienceMember['verdict']

type AudienceGridStyle = CSSProperties & {
  '--aud-list-width'?: string
  '--aud-detail-width'?: string
}

const VERDICT_OPTIONS: { value: VerdictFilter; label: string }[] = [
  { value: 'hot', label: 'Горячие' },
  { value: 'promising', label: 'Перспективные' },
  { value: 'not_fit', label: 'Не подходят' },
  { value: 'insufficient_data', label: 'Мало данных' },
]

const SCORABLE_VERDICTS: VerdictFilter[] = ['hot', 'promising', 'not_fit', 'insufficient_data']

const VERDICT_LABEL: Record<AudienceMember['verdict'], string> = {
  hot: 'Горячий',
  promising: 'Перспективный',
  not_fit: 'Не подходит',
  insufficient_data: 'Мало данных',
}

const SUMMARY_HELP: Record<string, string> = {
  'Участников': 'Все реальные участники аудитории проекта без ботов.',
  'Оценено': 'Сколько участников уже получили AI-оценку под текущий проект.',
  'Горячие': 'Есть явная релевантная потребность и хороший fit под клиента.',
  'Перспективные': 'Есть интерес или боль по теме, но уверенность ниже, чем у горячих.',
  'Не подходят': 'Уверенный негатив: конкурент, реклама или явно нецелевой профиль.',
  'Мало данных': 'Оценка недостаточно уверена или ещё не выполнялась. Неоценённые участники находятся в самом конце списка.',
}

const SUMMARY_VERDICT: Record<string, VerdictFilter | ''> = {
  'Участников': '',
  'Оценено': '',
  'Горячие': 'hot',
  'Перспективные': 'promising',
  'Не подходят': 'not_fit',
  'Мало данных': 'insufficient_data',
}

const AUD_LIST_WIDTH_KEY = 'sensor.audience.listWidth'
const AUD_DETAIL_WIDTH_KEY = 'sensor.audience.detailWidth'
const AUD_LIST_MIN = 480
const AUD_DETAIL_MIN = 340
const AUD_DETAIL_MAX = 620
const AUD_DETAIL_DEFAULT = 400

function clampDetailWidth(width: number): number {
  return Math.max(AUD_DETAIL_MIN, Math.min(AUD_DETAIL_MAX, Math.round(width)))
}

function useAudienceSplitResize() {
  const gridRef = useRef<HTMLDivElement>(null)
  const [listWidth, setListWidth] = useState<number | null>(() => {
    if (typeof window === 'undefined') return null
    const stored = Number(window.localStorage.getItem(AUD_LIST_WIDTH_KEY))
    return Number.isFinite(stored) && stored > 0 ? Math.max(AUD_LIST_MIN, Math.round(stored)) : null
  })
  const [detailWidth, setDetailWidth] = useState(() => {
    if (typeof window === 'undefined') return AUD_DETAIL_DEFAULT
    const stored = Number(window.localStorage.getItem(AUD_DETAIL_WIDTH_KEY))
    return Number.isFinite(stored) && stored > 0 ? clampDetailWidth(stored) : AUD_DETAIL_DEFAULT
  })

  useEffect(() => {
    if (listWidth != null) window.localStorage.setItem(AUD_LIST_WIDTH_KEY, String(listWidth))
  }, [listWidth])
  useEffect(() => {
    window.localStorage.setItem(AUD_DETAIL_WIDTH_KEY, String(detailWidth))
  }, [detailWidth])

  function startResize(e: ReactPointerEvent<HTMLDivElement>) {
    const box = gridRef.current?.getBoundingClientRect()
    if (!box) return
    e.preventDefault()
    const onMove = (event: PointerEvent) => {
      setListWidth(Math.max(AUD_LIST_MIN, Math.round(event.clientX - box.left)))
      setDetailWidth(clampDetailWidth(box.right - event.clientX - 8))
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const gridStyle: AudienceGridStyle = {
    ...(listWidth != null ? { '--aud-list-width': `${listWidth}px` } : {}),
    '--aud-detail-width': `${detailWidth}px`,
  }
  return { gridRef, gridStyle, startResize }
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).format(new Date(value))
}

function formatDateTime(value: string | null | undefined): string | null {
  if (!value) return null
  // PostgreSQL legacy rows are stored as naive UTC timestamps.  Treat them
  // as UTC rather than letting a browser reinterpret them in its local zone.
  const normalized = /(?:Z|[+-]\d{2}:\d{2})$/i.test(value) ? value : `${value}Z`
  const parsed = new Date(normalized)
  if (Number.isNaN(parsed.getTime())) return null
  const formatted = new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(parsed)
  return `${formatted} UTC`
}

function reachAddressability(campaign: ReachCampaignResponse | null | undefined): ReachAddressabilitySummary | null {
  if (!campaign) return null
  return campaign.addressability ?? campaign.addressability_breakdown ?? campaign.dry_run ?? null
}

function addressabilityCount(
  breakdown: ReachAddressabilitySummary | null | undefined,
  ...keys: (keyof ReachAddressabilitySummary)[]
): number | null {
  if (!breakdown) return null
  for (const key of keys) {
    const value = breakdown[key]
    if (typeof value === 'number') return value
  }
  return null
}

function deliveryAddressLabel(status: string | null | undefined): string {
  switch (status) {
    case 'username': return 'username найден'
    case 'id_access_hash': return 'ID + access_hash'
    case 'no_address': return 'нет адреса'
    case 'manual': return 'вручную'
    default: return 'адрес не проверен'
  }
}

function deliveryDmLabel(status: string | null | undefined): string {
  switch (status) {
    case 'unverified':
    case 'not_checked':
    case undefined:
    case null:
      return 'разрешение DM не проверено'
    case 'privacy_blocked': return 'DM запрещён настройками приватности'
    case 'available': return 'DM доступен'
    case 'flood_wait': return 'FloodWait'
    default: return status
  }
}

function reachDeliveryStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'sent': return 'Отправлено'
    case 'failed': return 'Не отправлено'
    case 'pending': return 'Ожидает отправки'
    case 'skipped': return 'Пропущено'
    default: return status || 'Статус неизвестен'
  }
}

function isManualDelivery(delivery: {
  manual_username: string | null
  manual_telegram_user_id: number | null
}): boolean {
  return Boolean(delivery.manual_username || delivery.manual_telegram_user_id != null)
}

/** Accept a pasted list: commas, spaces and line breaks are all separators. */
function parseReachManualRecipients(value: string): string[] {
  const seen = new Set<string>()
  return value
    .split(/[\s,]+/)
    .map((item) => item.trim().replace(/^@+/, ''))
    .filter((item) => {
      if (!item) return false
      const isTelegramId = /^\d+$/.test(item) && Number.isSafeInteger(Number(item)) && Number(item) > 0
      const isUsername = /^[A-Za-z][A-Za-z0-9_]{4,31}$/.test(item)
      if (!isTelegramId && !isUsername) return false
      const key = item.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

function sandboxStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'dry_run': return 'dry-run завершён'
    case 'running': return 'отправка выполняется'
    case 'completed': return 'тест завершён'
    case 'ready': return 'готово к проверке'
    case 'blocked': return 'заблокировано'
    case 'sent': return 'отправлено'
    case 'privacy_blocked': return 'личные сообщения запрещены'
    case 'flood_wait': return 'FloodWait · остановлено'
    case 'failed': return 'ошибка'
    default: return status || 'не запускалось'
  }
}

function sandboxTelegramAcceptanceLabel(
  accepted: boolean | null | undefined,
  messageId: number | null | undefined,
): string {
  if (accepted === true) return messageId ? `Telegram принял сообщение · №${messageId}` : 'Telegram принял сообщение'
  if (accepted === false) return 'Telegram не принял сообщение'
  return 'Telegram не подтвердил принятие'
}

function sandboxReadLabel(value: string | null | undefined, checked = false): string {
  if (value === 'read') return 'Прочитано'
  if (value === 'sent') return checked ? 'Не прочитано' : 'Прочтение ещё не проверено'
  if (value === 'not_sent') return 'Не отправлено'
  return 'Статус прочтения неизвестен'
}

function sandboxDeliveryEvidenceLabel(value: string | null | undefined): string {
  if (value === 'read') return 'Доставка подтверждена прочтением.'
  if (value === 'sent') return 'Сообщение принято Telegram; отдельный receipt доставки не предоставляется.'
  if (value === 'not_sent') return 'Сообщение не было отправлено.'
  return 'Telegram не вернул статус прочтения.'
}

function reachCampaignStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'draft': return 'черновик'
    case 'blocked': return 'заблокирована для массового запуска'
    case 'ready': return 'готова к массовому запуску'
    case 'completed': return 'завершена'
    case 'paused': return 'на паузе'
    case 'stopped': return 'остановлена'
    default: return status || 'неизвестный статус'
  }
}

const REACH_VERDICT_ORDER: Record<string, number> = {
  hot: 0,
  promising: 1,
  insufficient_data: 2,
  not_fit: 3,
}

function reachVerdictLabel(verdict: string | null | undefined): string {
  if (verdict === 'hot') return 'Горячий'
  if (verdict === 'promising') return 'Перспективный'
  if (verdict === 'not_fit') return 'Не подходит'
  if (verdict === 'insufficient_data') return 'Мало данных'
  return 'Без оценки'
}

function memberName(member: AudienceMember): string {
  return member.display_name?.trim() || (member.username ? `@${member.username}` : `ID ${member.telegram_user_id}`)
}

function contactLabel(member: AudienceMember): string {
  if (member.username) return `@${member.username}`
  return 'нет @username · личный чат можно открыть из сообщения'
}

function originalMessageAction(member: AudienceMember): { href: string; label: string; title: string } | null {
  const messageLink = member.evidence.find((item) => item.message_link)?.message_link
  if (!messageLink) return null
  return {
    href: messageLink,
    label: 'Открыть оригинал сообщения в Telegram',
    title: 'Открыть источник, из которого был собран этот участник',
  }
}

function personalContactAction(member: AudienceMember): { href: string; label: string; title: string } | null {
  if (member.username) {
    return {
      href: `https://t.me/${member.username.replace(/^@+/, '')}`,
      label: 'Написать пользователю в Telegram',
      title: 'Открыть личный чат в Telegram',
    }
  }

  return null
}

function ScoreBar({ label, value, muted = false }: { label: string; value: number; muted?: boolean }) {
  return (
    <div className="aud-score">
      <div className="aud-score__top">
        <span>{label}</span>
        <b>{value}</b>
      </div>
      <div className="aud-score__track">
        <i style={{ width: `${Math.max(0, Math.min(100, value))}%`, opacity: muted ? 0.62 : 1 }} />
      </div>
    </div>
  )
}

function SummaryStrip({
  summary,
  activeVerdicts,
  onSelect,
}: {
  summary?: {
    total_members: number
    scored: number
    hot: number
    promising: number
    not_fit: number
    insufficient_data: number
  }
  activeVerdicts: Set<VerdictFilter>
  onSelect: (value: VerdictFilter | '') => void
}) {
  const items = [
    ['Участников', summary?.total_members ?? 0],
    ['Оценено', summary?.scored ?? 0],
    ['Горячие', summary?.hot ?? 0],
    ['Перспективные', summary?.promising ?? 0],
    ['Не подходят', summary?.not_fit ?? 0],
    ['Мало данных', summary?.insufficient_data ?? 0],
  ] as const
  return (
    <div className="aud-summary">
      {items.map(([label, value]) => {
        const v = SUMMARY_VERDICT[label]
        const active = v === '' ? activeVerdicts.size === 0 : activeVerdicts.has(v as VerdictFilter)
        return (
          <button
            key={label}
            type="button"
            className={`aud-summary__item${active ? ' is-active' : ''}`}
            data-tip={SUMMARY_HELP[label]}
            onClick={() => onSelect(v)}
          >
            <span>{label}</span>
            <b>{value}</b>
          </button>
        )
      })}
    </div>
  )
}

function AudienceDetail({ member }: { member: AudienceMember | null }) {
  if (!member) {
    return (
      <aside className="aud-detail aud-detail--empty">
        <span>Выберите участника</span>
      </aside>
    )
  }

  const flags = [
    member.is_premium ? 'premium' : '',
    member.is_verified ? 'verified' : '',
    member.is_scam ? 'scam' : '',
    member.is_fake ? 'fake' : '',
  ].filter(Boolean)
  const originalAction = originalMessageAction(member)
  const personalAction = personalContactAction(member)
  const isUnscored = member.scored_at === null

  return (
    <aside className="aud-detail">
      <div className="aud-detail__head">
        <div>
          <span className={`aud-verdict aud-verdict--${member.verdict}`}>
            {VERDICT_LABEL[member.verdict]}
          </span>
          <h2>{memberName(member)}</h2>
          <p>{contactLabel(member)}</p>
          <div className="aud-actions">
            {originalAction && (
              <a
                className="aud-contact"
                href={originalAction.href}
                target="_blank"
                rel="noopener noreferrer"
                title={originalAction.title}
              >
                {originalAction.label}
              </a>
            )}
            {personalAction && (
              <a
                className="aud-contact aud-contact--secondary"
                href={personalAction.href}
                target="_blank"
                rel="noopener noreferrer"
                title={personalAction.title}
              >
                {personalAction.label}
              </a>
            )}
            {!personalAction && (
              <span
                className="aud-contact aud-contact--disabled"
                title="Telegram не поддерживает прямую ссылку на личный чат пользователя без @username. Откройте исходное сообщение и нажмите на имя автора."
              >
                Перейдите в ЛС из чата
              </span>
            )}
          </div>
        </div>
        <div className="aud-overall">
          <b>{isUnscored ? '—' : member.overall_score}</b>
          <span>{isUnscored ? 'не оценён' : '/100'}</span>
        </div>
      </div>

      {isUnscored && (
        <div className="aud-note aud-note--muted">
          <span>Оценка ещё не выполнялась</span>
          <p>Участник сохранён в Audience, но пока не получил AI-рейтинг. Он находится в конце списка.</p>
        </div>
      )}

      {flags.length > 0 && (
        <div className="aud-flags">
          {flags.map((flag) => (
            <span key={flag}>{flag}</span>
          ))}
        </div>
      )}

      {member.bio && <p className="aud-bio">{member.bio}</p>}
      {member.portrait && <p className="aud-portrait">{member.portrait}</p>}

      <div className="aud-scores">
        <ScoreBar label="Интерес" value={member.interest_score} />
        <ScoreBar label="Похож на клиента" value={member.client_fit_score} />
        <ScoreBar label="Свежесть" value={member.recency_score} />
        <ScoreBar label="Шум" value={member.noise_score} muted />
      </div>

      {member.reason && (
        <div className="aud-note">
          <span>Обоснование</span>
          <p>{member.reason}</p>
        </div>
      )}

      <div className="aud-evidence">
        <span>Доказательства</span>
        {member.evidence.length > 0 ? (
          member.evidence.map((item) => (
            <a
              key={`${item.audience_message_id}-${item.quote}`}
              href={item.message_link ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              className={!item.message_link ? 'is-disabled' : undefined}
            >
              <b>{item.group_title || formatDate(item.message_datetime)}</b>
              <em>“{item.quote}”</em>
            </a>
          ))
        ) : (
          <p>—</p>
        )}
      </div>
    </aside>
  )
}

function projectOptions(projects: ProjectResponse[] | undefined) {
  const numbers = buildProjectNumberMap(projects)
  return (
    projects?.map((project) => ({
      value: String(project.id),
      label: projectLabelById(project.id, projects, numbers),
    })) ?? []
  )
}

const REACH_WORKFLOW_STAGES: Array<{ status: ReachConversationStatus; label: string }> = [
  { status: 'queued', label: 'В очереди' },
  { status: 'permission_requested', label: 'Отправлено' },
  { status: 'replied', label: 'Ответили' },
  { status: 'needs_review', label: 'Проверить вручную' },
  { status: 'escalated', label: 'Эскалировано' },
]

const REACH_WORKFLOW_FINAL_STAGES: Array<{ status: ReachConversationStatus; label: string }> = [
  { status: 'stopped', label: 'Отказ' },
  { status: 'opted_out', label: 'Не писать' },
  { status: 'unreachable', label: 'Недоступен' },
]

function reachConversationStatusLabel(status: string | null | undefined): string {
  return [...REACH_WORKFLOW_STAGES, ...REACH_WORKFLOW_FINAL_STAGES]
    .find((item) => item.status === status)?.label ?? status ?? 'Без статуса'
}

function conversationRecipient(conversation: Pick<ReachConversationSummary, 'contact_display_name' | 'contact_username' | 'contact_telegram_user_id'>): string {
  return conversation.contact_display_name?.trim()
    || (conversation.contact_username ? `@${conversation.contact_username.replace(/^@+/, '')}` : null)
    || (conversation.contact_telegram_user_id ? `ID ${conversation.contact_telegram_user_id}` : 'Получатель без имени')
}

function ReachWorkflow({
  projectId,
  projectName,
}: {
  projectId: number
  projectName: string
}) {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<ReachConversationStatus | ''>('')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [replyText, setReplyText] = useState('')
  const funnel = useQuery({
    queryKey: ['reach-funnel', projectId],
    queryFn: () => api.getReachFunnel(projectId),
    retry: false,
  })
  const conversations = useQuery({
    queryKey: ['reach-conversations', projectId, statusFilter],
    queryFn: () => api.getReachConversations(projectId, statusFilter || undefined),
    retry: false,
  })
  const list = conversations.data ?? funnel.data?.conversations ?? []
  useEffect(() => {
    if (!list.some((item) => item.id === selectedId)) setSelectedId(list[0]?.id ?? null)
  }, [list, selectedId])
  const detail = useQuery({
    queryKey: ['reach-conversation', projectId, selectedId],
    queryFn: () => api.getReachConversation(projectId, selectedId as number),
    enabled: selectedId != null,
    retry: false,
  })
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['reach-funnel', projectId] })
    void queryClient.invalidateQueries({ queryKey: ['reach-conversations', projectId] })
    void queryClient.invalidateQueries({ queryKey: ['reach-conversation', projectId, selectedId] })
  }
  const transition = useMutation({
    mutationFn: ({ id, status }: { id: number; status: ReachConversationStatus }) =>
      api.transitionReachConversation(projectId, id, { status }),
    onSuccess: refresh,
  })
  // registerIncoming removed — manual reply recording replaced by automated reply watcher.
  const sendReply = useMutation({
    mutationFn: async () => {
      // Record the reply as a system note in the conversation for now.
      // Backend sender runtime will pick it up and send via Telethon.
      return api.transitionReachConversation(projectId, selectedId as number, {
        status: 'escalated',
        note: replyText.trim(),
      })
    },
    onSuccess: () => {
      setReplyText('')
      refresh()
    },
  })
  const doNotContact = useMutation({
    mutationFn: async (conversation: ReachConversationResponse) => {
      await api.markReachContactDoNotContact(projectId, conversation.contact_id, 'Просьба не писать / ручная остановка')
    },
    onSuccess: refresh,
  })
  const selected = detail.data ?? list.find((item) => item.id === selectedId) ?? null
  const counts = funnel.data?.counts ?? {}
  const isLoading = funnel.isLoading || conversations.isLoading

  return (
    <div className="reach-flow">
      <header className="reach-flow__header">
        <div>
          <p className="reach-section__eyebrow">Диалоги</p>
          <h3>Контакты проекта «{projectName}»</h3>
          <p>Выберите контакт, чтобы увидеть историю переписки.</p>
        </div>
        <span className="reach-flow__total">{funnel.data?.total ?? list.length} контактов</span>
      </header>
      <div className="reach-funnel" role="group" aria-label="Статусы диалогов">
        <button type="button" className={!statusFilter ? 'is-active' : ''} onClick={() => setStatusFilter('')}>
          <b>Все</b><span>{funnel.data?.total ?? list.length}</span>
        </button>
        {REACH_WORKFLOW_STAGES.map((stage) => (
          <button
            key={stage.status}
            type="button"
            className={statusFilter === stage.status ? 'is-active' : ''}
            onClick={() => setStatusFilter(stage.status)}
          >
            <b>{stage.label}</b><span>{counts[stage.status] ?? 0}</span>
          </button>
        ))}
      </div>
      <div className="reach-flow__body">
        <aside className="reach-flow-list" aria-label="Очередь контактов">
          <div className="reach-flow-list__title">
            <span>{statusFilter ? reachConversationStatusLabel(statusFilter) : 'Все контакты'}</span>
            {isLoading && <small>Обновляю…</small>}
          </div>
          {conversations.isError && !funnel.data && <p className="reach-flow__error">Воронка пока недоступна: {conversations.error instanceof Error ? conversations.error.message : 'ошибка API'}</p>}
          {!isLoading && !list.length && <p className="reach-flow__empty">Очередь пуста. Откройте кампанию и добавьте подходящих получателей в рабочую очередь.</p>}
          {list.map((conversation) => (
            <button
              key={conversation.id}
              type="button"
              className={`reach-flow-list__item${selectedId === conversation.id ? ' is-selected' : ''}`}
              onClick={() => setSelectedId(conversation.id)}
            >
              <span className="reach-flow-list__name">{conversationRecipient(conversation)}</span>
              <span className="reach-flow-list__meta">
                {conversation.score != null ? `${conversation.score}/100 · ` : ''}{reachConversationStatusLabel(conversation.status)}
              </span>
            </button>
          ))}
        </aside>
        <section className="reach-flow-detail" aria-live="polite">
          {!selected && <div className="reach-flow__empty">Выберите контакт, чтобы увидеть контекст и историю.</div>}
          {selected && (
            <>
              <header className="reach-contact-head">
                <div>
                  <span className={`reach-status reach-status--${selected.status}`}>{reachConversationStatusLabel(selected.status)}</span>
                  <h4>{conversationRecipient(selected)}</h4>
                  <p>{selected.contact_username ? `@${selected.contact_username.replace(/^@+/, '')}` : 'Нет публичного @username'}{selected.score != null ? ` · ${selected.score}/100` : ''}{selected.hybrid ? ' · гибридный подбор' : ''}</p>
                </div>
                {selected.verdict && <span className={`aud-verdict aud-verdict--${selected.verdict}`}>{reachVerdictLabel(selected.verdict)}</span>}
              </header>
              <div className="reach-contact-context">
                {selected.source_title && <p><b>Источник:</b> {selected.source_title}</p>}
                {selected.source_message && <blockquote>{selected.source_message}</blockquote>}
                {selected.portrait && <p><b>Портрет:</b> {selected.portrait}</p>}
                {selected.reason && <p><b>Почему в очереди:</b> {selected.reason}</p>}
                {selected.project_ids?.length > 1 && <p><b>Проекты:</b> {selected.project_ids.join(', ')}</p>}
              </div>
              <div className="reach-contact-actions" aria-label="Действия с диалогом">
                <div>
                  {selected.status === 'replied' && <button type="button" className="btn btn--ai btn--sm" disabled={transition.isPending} onClick={() => transition.mutate({ id: selected.id, status: 'escalated' })}>Эскалировать на менеджера</button>}
                  {!['escalated', 'stopped', 'opted_out', 'unreachable'].includes(selected.status) && <button type="button" className="btn btn--ghost btn--sm" disabled={transition.isPending} onClick={() => transition.mutate({ id: selected.id, status: 'stopped' })}>Отказ</button>}
                  {['stopped', 'escalated'].includes(selected.status) && <button type="button" className="btn btn--ghost btn--sm" disabled={transition.isPending} onClick={() => transition.mutate({ id: selected.id, status: 'replied' })}>Вернуть в работу</button>}
                  <button type="button" className="btn btn--ghost btn--sm" disabled={doNotContact.isPending} onClick={() => doNotContact.mutate(selected as ReachConversationResponse)}>Не писать этому контакту</button>
                </div>
                {(transition.isError || doNotContact.isError) && <p className="reach-flow__error">Не удалось обновить статус. Попробуйте ещё раз.</p>}
              </div>
              <section className="reach-contact-history">
                <h5>История диалога</h5>
                {detail.isLoading && <p>Загружаю историю…</p>}
                {detail.data?.messages?.length ? detail.data.messages.map((message) => (
                  <article key={message.id} className={`reach-contact-message reach-contact-message--${message.direction}`}>
                    <span>{message.direction === 'inbound' ? 'Входящее' : message.direction === 'outbound' ? 'Исходящее' : 'Системное'} · {formatDateTime(message.occurred_at) ?? 'время неизвестно'}</span>
                    <p>{message.rendered_text}</p>
                  </article>
                )) : <p>Сообщений пока нет.</p>}
                <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 650 }}>Написать ответ контакту</span>
                  <textarea
                    value={replyText}
                    onChange={(event) => setReplyText(event.target.value)}
                    rows={3}
                    placeholder="Текст ответа — будет поставлен в очередь на отправку (в приоритете перед первыми сообщениями)"
                    style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', resize: 'vertical' }}
                  />
                  <div>
                    <button type="button" className="btn btn--ai btn--sm" disabled={!replyText.trim() || sendReply.isPending} onClick={() => sendReply.mutate()}>
                      {sendReply.isPending ? 'Ставлю в очередь…' : 'Поставить в очередь на отправку'}
                    </button>
                    {sendReply.isSuccess && <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--accent)' }}>✓ В очереди</span>}
                    {sendReply.isError && <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--danger, #b42318)' }}>Ошибка: {sendReply.error instanceof Error ? sendReply.error.message : 'неизвестно'}</span>}
                  </div>
                </div>
              </section>
            </>
          )}
        </section>
      </div>
      <details className="reach-flow__finals">
        <summary>Неподходящие и остановленные</summary>
        <div>{REACH_WORKFLOW_FINAL_STAGES.map((stage) => <span key={stage.status}>{stage.label}: <b>{counts[stage.status] ?? 0}</b></span>)}</div>
      </details>
    </div>
  )
}

export function AudiencePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [verdicts, setVerdicts] = useState<Set<VerdictFilter>>(new Set())
  const [onlyWithUsername, setOnlyWithUsername] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [reachOpen, setReachOpen] = useState(false)
  const [reachProjectId, setReachProjectId] = useState<number | null>(null)
  const [hybridModalOpen, setHybridModalOpen] = useState(false)
  const [hybridSelected, setHybridSelected] = useState<number[]>([])
  const [hybridName, setHybridName] = useState('Гибрид — кофе + сублиматы')
  const [hybridVariations, setHybridVariations] = useState(10)
  const [hybridResult, setHybridResult] = useState<{ campaign_id: number; hybrid_recipient_count: number; moved_from_campaigns: Record<string, number> } | null>(null)
  const [reachCreateOpen, setReachCreateOpen] = useState(false)
  const [reachDetailTab, setReachDetailTab] = useState<'recipients' | 'dialogs' | 'message' | 'test'>('recipients')
  const [reachName, setReachName] = useState('Новая рассылка')
  const [reachVerdicts, setReachVerdicts] = useState<string[]>([])
  const [reachTemplateIds, setReachTemplateIds] = useState<number[]>([])
  const [reachTemplateTag, setReachTemplateTag] = useState('')
  const [reachTemplateStage, setReachTemplateStage] = useState<'permission' | 'offer'>('permission')
  const [reachTemplateText, setReachTemplateText] = useState('')
  const [reachTemplateMedia, setReachTemplateMedia] = useState<{ data: string; mime: string } | null>(null)
  const [reachTemplateSavedNotice, setReachTemplateSavedNotice] = useState<string | null>(null)
  const [reachQueueRefreshNotice, setReachQueueRefreshNotice] = useState<string | null>(null)
  const [reachResult, setReachResult] = useState<ReachCampaignResponse | null>(null)
  const [reachManualRecipient, setReachManualRecipient] = useState('')
  const [reachManualAddSummary, setReachManualAddSummary] = useState<string | null>(null)
  const [reachReviewed, setReachReviewed] = useState(false)
  const [reachSandboxReviewed, setReachSandboxReviewed] = useState(false)
  const [reachSandboxResult, setReachSandboxResult] = useState<ReachSandboxDryRunResponse | null>(null)
  const [reachSendResult, setReachSendResult] = useState<ReachSandboxSendResponse | null>(null)
  const [reachStatusResult, setReachStatusResult] = useState<ReachSandboxSendResponse | null>(null)
  const { user } = useAuth()
  const audienceLocked = !!user && !user.has_audience
  const [exporting, setExporting] = useState(false)
  const { gridRef, gridStyle, startResize } = useAudienceSplitResize()

  const resetReachRunState = () => {
    setReachResult(null)
    setReachDetailTab('recipients')
    setReachManualRecipient('')
    setReachManualAddSummary(null)
    setReachQueueRefreshNotice(null)
    setReachReviewed(false)
    setReachSandboxReviewed(false)
    setReachSandboxResult(null)
    setReachSendResult(null)
    setReachStatusResult(null)
    setReachDetailTab('recipients')
  }

  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: api.getProjects,
  })

  const projectId = useMemo(() => {
    const raw = Number(searchParams.get('project'))
    if (Number.isFinite(raw) && raw > 0) return raw
    return projects?.[0]?.id ?? null
  }, [projects, searchParams])

  // Reach has its own explicit project scope.  The page project remains the
  // default, but changing it here must never silently mix templates or
  // recipients from another project.
  useEffect(() => {
    if (reachProjectId == null && projectId != null) setReachProjectId(projectId)
  }, [projectId, reachProjectId])

  useEffect(() => {
    setReachTemplateIds([])
    resetReachRunState()
    setReachCreateOpen(false)
  }, [reachProjectId])

  useEffect(() => {
    if (!projectId || searchParams.get('project')) return
    setSearchParams({ project: String(projectId) }, { replace: true })
  }, [projectId, searchParams, setSearchParams])

  const { data: summary } = useQuery({
    queryKey: ['audience-summary', projectId],
    queryFn: () => api.getAudienceSummary(projectId as number),
    enabled: !!projectId && !audienceLocked,
  })
  const queryClient = useQueryClient()

  // Reach is intentionally exposed by the API only on the dev environment.
  // A 404/403 here therefore keeps the production UI unchanged.
  const { data: reachAvailability } = useQuery({
    queryKey: ['reach-availability', reachProjectId],
    queryFn: () => api.getReachAvailability(reachProjectId as number),
    enabled: !!reachProjectId && !audienceLocked,
    retry: false,
  })
  const reachEnabled = reachAvailability?.enabled === true

  // Load campaigns for ALL projects (not just the selected one) so the
  // sidebar can show every campaign in the organization.
  const reachAllCampaigns = useQuery({
    queryKey: ['reach-campaigns-all', projects],
    queryFn: async () => {
      const results: Awaited<ReturnType<typeof api.getReachCampaigns>> = []
      for (const project of projects ?? []) {
        try {
          const campaigns = await api.getReachCampaigns(project.id)
          results.push(...campaigns)
        } catch { /* project may not have reach */ }
      }
      return results
    },
    enabled: reachOpen && reachEnabled && !!projects?.length,
    retry: false,
  })
  const createReach = useMutation({
    mutationFn: () => api.createReachCampaign(reachProjectId as number, {
      name: reachName,
      verdicts: reachVerdicts,
      template_ids: reachTemplateIds,
    }),
    onSuccess: (campaign) => {
      setReachResult(campaign)
      setReachCreateOpen(false)
      setReachDetailTab('recipients')
      setReachTemplateSavedNotice(null)
      setReachManualRecipient('')
      setReachManualAddSummary(null)
      setReachReviewed(false)
      setReachSandboxReviewed(false)
      setReachSandboxResult(null)
      setReachSendResult(null)
      setReachStatusResult(null)
      void queryClient.invalidateQueries({ queryKey: ['reach-campaigns', reachProjectId] })
      void queryClient.invalidateQueries({ queryKey: ['reach-campaigns-all', projects] })
    },
  })
  const createHybrid = useMutation({
    mutationFn: () => api.createHybridCampaign(hybridSelected, hybridName, hybridVariations),
    onSuccess: (result) => {
      setHybridResult(result)
      void queryClient.invalidateQueries({ queryKey: ['reach-campaigns-all', projects] })
    },
  })
  const createReachTemplate = useMutation({
    mutationFn: () => api.createReachTemplate(reachProjectId as number, {
      tag: reachTemplateTag,
      stage: reachTemplateStage,
      text: reachTemplateText,
      media_data: reachTemplateMedia?.data ?? null,
      media_mime_type: reachTemplateMedia?.mime ?? null,
    }),
    onSuccess: (template) => {
      setReachTemplateTag('')
      setReachTemplateStage('permission')
      setReachTemplateText('')
      setReachTemplateMedia(null)
      setReachTemplateIds((current) => current.includes(template.id) ? current : [...current, template.id])
      resetReachRunState()
      setReachTemplateSavedNotice(`Вариант «${template.tag}» сохранён. Он будет использован только в новой кампании — старые кампании не меняются.`)
      void queryClient.invalidateQueries({ queryKey: ['reach-availability', reachProjectId] })
    },
  })
  const preflightReach = useMutation({
    mutationFn: (campaignId: number) => api.preflightReachCampaign(reachProjectId as number, campaignId),
    onSuccess: (campaign) => {
      setReachResult(campaign)
      void queryClient.invalidateQueries({ queryKey: ['reach-campaigns-all', projects] })
    },
  })
  const pauseReach = useMutation({
    mutationFn: (campaignId: number) => api.pauseReachCampaign(reachProjectId as number, campaignId),
    onSuccess: (campaign) => {
      setReachResult(campaign)
      setReachQueueRefreshNotice('Рассылка поставлена на паузу. Новые сообщения больше не будут запускаться; теперь можно обновить очередь.')
      void queryClient.invalidateQueries({ queryKey: ['reach-campaigns-all', projects] })
    },
  })
  const refreshReachQueue = useMutation({
    mutationFn: (campaignId: number) => api.refreshReachCampaignQueue(reachProjectId as number, campaignId),
    onSuccess: (result) => {
      setReachResult(result)
      setReachQueueRefreshNotice(`Очередь обновлена: +${result.added}, исключено ${result.excluded}, перепланировано ${result.rescheduled}. Всего по текущим правилам: ${result.candidate_total}.`)
      setReachSandboxResult(null)
      setReachSendResult(null)
      setReachStatusResult(null)
      void queryClient.invalidateQueries({ queryKey: ['reach-campaigns-all', projects] })
      void queryClient.invalidateQueries({ queryKey: ['reach-deliveries', reachProjectId, result.id] })
      void queryClient.invalidateQueries({ queryKey: ['reach-dry-run', reachProjectId, result.id] })
    },
  })
  const reachQueueError = pauseReach.error ?? refreshReachQueue.error
  const deleteReachCampaign = useMutation({
    mutationFn: (campaignId: number) => api.deleteReachCampaign(reachProjectId as number, campaignId),
    onSuccess: (_value, campaignId) => {
      if (reachResult?.id === campaignId) resetReachRunState()
      void queryClient.invalidateQueries({ queryKey: ['reach-campaigns', reachProjectId] })
    },
  })
  const reachDeliveries = useQuery({
    queryKey: ['reach-deliveries', reachProjectId, reachResult?.id],
    queryFn: () => api.getReachDeliveries(reachProjectId as number, reachResult!.id),
    enabled: !!reachProjectId && !!reachResult?.id,
    retry: false,
  })
  const reachDryRun = useQuery({
    queryKey: ['reach-dry-run', reachProjectId, reachResult?.id],
    queryFn: () => api.getReachDryRun(reachProjectId as number, reachResult!.id),
    enabled: !!reachProjectId && !!reachResult?.id,
    retry: false,
  })
  // reachSandboxHistory removed — history tab no longer exists.
  const deleteAllReach = useMutation({
    mutationFn: () => api.deleteAllReachDeliveries(reachProjectId as number, reachResult!.id),
    onSuccess: (value) => {
      setReachSandboxResult(null)
      setReachSendResult(null)
      setReachStatusResult(null)
      setReachResult((current) => current ? { ...current, recipient_count: value.recipient_count } : current)
      void queryClient.invalidateQueries({ queryKey: ['reach-deliveries', reachProjectId, reachResult?.id] })
      void queryClient.invalidateQueries({ queryKey: ['reach-dry-run', reachProjectId, reachResult?.id] })
    },
  })
  const deleteReach = useMutation({
    mutationFn: (deliveryId: number) => api.deleteReachDelivery(reachProjectId as number, reachResult!.id, deliveryId),
    onSuccess: (value) => {
      setReachSandboxResult(null)
      setReachSendResult(null)
      setReachStatusResult(null)
      setReachResult((current) => current ? { ...current, recipient_count: value.recipient_count } : current)
      void queryClient.invalidateQueries({ queryKey: ['reach-deliveries', reachProjectId, reachResult?.id] })
      void queryClient.invalidateQueries({ queryKey: ['reach-dry-run', reachProjectId, reachResult?.id] })
    },
  })
  const addReachManual = useMutation({
    mutationFn: () => {
      const recipients = parseReachManualRecipients(reachManualRecipient)
      if (!recipients.length) throw new Error('Введите @username или Telegram ID')
      return api.addReachManualDeliveries(reachProjectId as number, reachResult!.id, recipients)
    },
    onSuccess: (value) => {
      setReachManualRecipient('')
      setReachManualAddSummary(
        value.skipped.length
          ? `Добавлено: ${value.added}. Пропущено: ${value.skipped.length} (дубликаты или некорректные адреса).`
          : `Добавлено: ${value.added}.`,
      )
      setReachSandboxResult(null)
      setReachSendResult(null)
      setReachStatusResult(null)
      setReachResult((current) => current ? {
        ...current,
        recipient_count: value.recipient_count ?? current.recipient_count + value.deliveries.length,
      } : current)
      void queryClient.invalidateQueries({ queryKey: ['reach-deliveries', reachProjectId, reachResult?.id] })
      void queryClient.invalidateQueries({ queryKey: ['reach-dry-run', reachProjectId, reachResult?.id] })
    },
  })
  // addReachDeliveryToWorkflow removed — conversations are now shown inline in the dialogs tab.
  // const _addReachDeliveryToWorkflow = useMutation({ ... })
  const runReachSandbox = useMutation({
    mutationFn: () => api.runReachSandboxDryRun(reachProjectId as number, reachResult!.id, { confirmation: reachSandboxReviewed }),
    onSuccess: (result) => setReachSandboxResult(result),
  })
  const sendReachSandbox = useMutation({
    mutationFn: () => api.sendReachSandbox(reachProjectId as number, reachResult!.id, {
      confirmation: reachSandboxReviewed,
      delay_min_seconds: 120,
      delay_max_seconds: 180,
    }),
    onSuccess: (result) => {
      setReachSendResult(result)
      setReachStatusResult(null)
    },
  })
  const checkReachSandboxStatus = useMutation({
    mutationFn: () => api.checkReachSandboxStatus(
      reachProjectId as number,
      reachResult!.id,
      reachSendResult!.id,
    ),
    onSuccess: (result) => setReachStatusResult(result),
  })
  const reachSenderAccounts = useQuery({
    queryKey: ['reach-sender-accounts', reachProjectId],
    queryFn: () => api.getReachSenderAccounts(reachProjectId as number),
    // This is read-only evidence: the server still chooses senders itself.
    enabled: !!reachProjectId && !!reachResult?.id,
    retry: false,
  })
  const pastedManualRecipients = useMemo(
    () => parseReachManualRecipients(reachManualRecipient),
    [reachManualRecipient],
  )
  const reachSortedDeliveries = useMemo(() => (
    [...(reachDeliveries.data ?? [])].sort((a, b) => {
      const aRank = a.verdict ? (REACH_VERDICT_ORDER[a.verdict] ?? 4) : 4
      const bRank = b.verdict ? (REACH_VERDICT_ORDER[b.verdict] ?? 4) : 4
      if (aRank !== bRank) return aRank - bRank
      if ((a.score ?? -1) !== (b.score ?? -1)) return (b.score ?? -1) - (a.score ?? -1)
      return (a.display_name || a.username || a.manual_username || '').localeCompare(
        b.display_name || b.username || b.manual_username || '',
        'ru',
      )
    })
  ), [reachDeliveries.data])

  useEffect(() => {
    if (!reachAvailability) return
    setReachTemplateIds((current) => current.length
      ? current.filter((id) => reachAvailability.templates.some((template) => template.id === id))
      : reachAvailability.templates.map((template) => template.id))
  }, [reachAvailability])

  const verdictList = useMemo(() => Array.from(verdicts), [verdicts])
  const { data: scores, isLoading: scoresLoading } = useQuery({
    queryKey: ['audience-scores', projectId, verdictList.join(','), onlyWithUsername],
    queryFn: () => api.getAudienceScores(projectId as number, {
      verdicts: verdictList,
      has_username: onlyWithUsername,
      // Audience is a project worklist, not a top-100 preview.  Load the
      // complete scoped list so unrated accounts are visible at the end.
      page_size: 10000,
    }),
    enabled: !!projectId && !audienceLocked,
  })

  useEffect(() => {
    const first = scores?.items[0]?.id ?? null
    if (!scores?.items.some((item) => item.id === selectedId)) {
      setSelectedId(first)
    }
  }, [scores, selectedId])

  // Cross-process scoring job: enqueue via POST, then poll for live progress.
  const { data: activeJob } = useQuery({
    queryKey: ['audience-job', projectId],
    queryFn: () => api.getActiveAudienceJob(projectId as number),
    enabled: !!projectId && !audienceLocked,
    refetchInterval: (q) => {
      const s = q.state.data?.status
      return s === 'running' || s === 'requested' ? 2500 : false
    },
  })
  const jobRunning = activeJob?.status === 'running' || activeJob?.status === 'requested'
  const selectedScorableVerdicts = useMemo(
    () => verdictList.filter((verdict) => SCORABLE_VERDICTS.includes(verdict)),
    [verdictList],
  )
  const scoreVerdicts = selectedScorableVerdicts.length
    ? selectedScorableVerdicts
    : verdictList.length === 0
      ? SCORABLE_VERDICTS
      : []
  const startScore = useMutation({
    mutationFn: () => api.startAudienceScoring(projectId as number, scoreVerdicts),
    onSuccess: (job) => queryClient.setQueryData(['audience-job', projectId], job),
  })
  const cancelJob = useMutation({
    mutationFn: () => api.cancelAudienceJob(projectId as number),
    onSuccess: (job) => queryClient.setQueryData(['audience-job', projectId], job),
  })
  // When a job finishes, refresh the scored list + summary.
  const prevJobStatus = useRef<string | undefined>(undefined)
  useEffect(() => {
    const s = activeJob?.status
    const prev = prevJobStatus.current
    if ((prev === 'running' || prev === 'requested') && (s === 'done' || s === 'failed' || s === 'canceled')) {
      void queryClient.invalidateQueries({ queryKey: ['audience-scores', projectId] })
      void queryClient.invalidateQueries({ queryKey: ['audience-summary', projectId] })
    }
    prevJobStatus.current = s
  }, [activeJob?.status, projectId, queryClient])

  const selected = scores?.items.find((item) => item.id === selectedId) ?? null
  const options = projectOptions(projects)
  const summaryKey = summary
    ? `${summary.total_members}-${summary.scored}-${summary.pending}-${summary.hot}-${summary.promising}-${summary.not_fit}-${summary.insufficient_data}`
    : 'empty'
  const projectSelectKey = `${projectId ?? 'none'}-${options.map((option) => option.value).join('-')}`
  const pendingCount = useMemo(() => {
    if (!summary) return 0
    // With no verdict filter, show actual work left rather than the size of
    // all scored buckets.  The scorer skips unchanged members on a retry, so
    // showing 907 here after a completed run is misleading.
    if (verdictList.length === 0) return summary.pending ?? 0
    return scoreVerdicts.reduce((count, verdict) => {
      if (verdict === 'hot') return count + summary.hot
      if (verdict === 'promising') return count + summary.promising
      if (verdict === 'not_fit') return count + summary.not_fit
      if (verdict === 'insufficient_data') {
        // "Мало данных" bucket + members that have never been scored.
        const unscored = Math.max(0, (summary.total_members ?? 0) - (summary.scored ?? 0))
        return count + (summary.insufficient_data ?? 0) + unscored
      }
      return count
    }, 0)
  }, [summary, scoreVerdicts, verdictList.length])
  const hasUnscored = summary ? (summary.total_members ?? 0) - (summary.scored ?? 0) > 0 : false
  const startScoreLabel =
    pendingCount === 0
      ? 'Всё оценено'
      : hasUnscored && summary && summary.scored === 0
        ? `Оценить ${pendingCount}`
        : `Дооценить ${pendingCount}`
  const pendingLabel = scoreVerdicts.length
    ? `К оценке: ${pendingCount}`
    : 'К оценке: 0'
  const canStartScore = pendingCount > 0

  async function handleExport() {
    if (!projectId) return
    setExporting(true)
    try {
      const blob = await api.exportAudience(projectId, { has_username: onlyWithUsername })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `sensor_audience_project_${projectId}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } finally {
      setExporting(false)
    }
  }

  if (audienceLocked) {
    return (
      <section className="audience-view">
        <div className="aud-head">
          <div>
            <h1>Аудитория</h1>
            <p>Активные участники ваших источников с AI-оценкой под проект</p>
          </div>
        </div>
        <UpsellNotice
          title="База аудитории доступна на Pro"
          description="Собираем активных участников ваших источников и оцениваем каждого под проект: портрет, доказательства и вердикт «горячий / перспективный». Подключите Pro, чтобы открыть базу аудитории."
          tier="Pro"
        />
      </section>
    )
  }

  return (
    <section className="audience-view">
      <div className="aud-head">
        <div>
          <h1>Аудитория</h1>
          <p key={`aud-meta-${summaryKey}`}>
            {summary
              ? `${summary.total_members} участников · ${summary.scored} оценено · ${Math.max(0, summary.total_members - summary.scored)} без оценки`
              : '—'}
          </p>
        </div>
        <div className="aud-head__controls">
          <Select
            key={`aud-project-${projectSelectKey}`}
            value={projectId ? String(projectId) : ''}
            onChange={(value) => {
              setSelectedId(null)
              setSearchParams({ project: value }, { replace: true })
            }}
            options={options}
            disabled={!options.length}
            ariaLabel="Проект"
            style={{ minWidth: 210, maxWidth: 420 }}
          />
          <div className="aud-verdict-chips" role="group" aria-label="Вердикты">
            {VERDICT_OPTIONS.map((opt) => {
              const on = verdicts.has(opt.value)
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`aud-verdict-chip aud-verdict-chip--${opt.value}${on ? ' is-on' : ''}`}
                  aria-pressed={on}
                  onClick={() => {
                    setSelectedId(null)
                    setVerdicts((prev) => {
                      const next = new Set(prev)
                      if (next.has(opt.value)) next.delete(opt.value)
                      else next.add(opt.value)
                      return next
                    })
                  }}
                >
                  {opt.label}
                </button>
              )
            })}
            {verdicts.size > 0 && (
              <button
                type="button"
                className="aud-verdict-chip aud-verdict-chip--clear"
                onClick={() => { setSelectedId(null); setVerdicts(new Set()) }}
                title="Сбросить фильтр"
              >
                Сбросить
              </button>
            )}
          </div>
          <button
            type="button"
            className={`aud-username-filter${onlyWithUsername ? ' is-on' : ''}`}
            aria-pressed={onlyWithUsername}
            title="Показывать только участников с публичным @username, которым можно написать напрямую в Telegram"
            onClick={() => {
              setSelectedId(null)
              setOnlyWithUsername((value) => !value)
            }}
          >
            <span aria-hidden="true">✓</span>
            Только с @username
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={handleExport}
            disabled={exporting || !scores?.items.length}
            title="Скачать оценённых участников в CSV"
          >
            {exporting ? '...' : 'Скачать CSV'}
          </button>
          {reachEnabled && (
            <button
              type="button"
              className="btn btn--ai btn--sm"
              onClick={() => setReachOpen((value) => !value)}
              title="Подготовить dev-рассылку по оценённой аудитории"
            >
              📨 Рассылка
            </button>
          )}
        </div>
      </div>

      {reachOpen && reachEnabled && (
        <div className="reach-workspace">
          <div className="reach-workspace__header">
            <div>
              <p className="reach-workspace__eyebrow">SENSOR.Reach</p>
              <h2>Рассылка</h2>
              <p className="reach-workspace__subtitle">Кампании по всем проектам</p>
            </div>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setReachOpen(false)}>Закрыть</button>
          </div>
          <div className="reach-workspace__body">
          <aside className="reach-campaign-list" aria-label="Список кампаний">
            <div className="reach-campaign-list__header">
              <span className="reach-campaign-list__title">Кампании</span>
              <div className="reach-campaign-list__actions" aria-label="Действия с кампаниями">
                <button
                  type="button"
                  className="reach-campaign-list__action reach-campaign-list__action--create"
                  onClick={() => {
                    resetReachRunState()
                    setReachCreateOpen(true)
                  }}
                >
                  <span aria-hidden="true">+</span>
                  Новая
                </button>
                <button
                  type="button"
                  className="reach-campaign-list__action reach-campaign-list__action--hybrid"
                  onClick={() => setHybridModalOpen(true)}
                  title="Создать гибридную кампанию из пересечений"
                >
                  <span aria-hidden="true">↔</span>
                  Гибрид
                </button>
              </div>
            </div>
            <div className="reach-campaign-list__label">{reachAllCampaigns.data?.length ?? 0} сохранённых кампаний</div>
            {reachAllCampaigns.isLoading && <span style={{ fontSize: 12, color: 'var(--muted)' }}>Загружаю кампании…</span>}
            {reachAllCampaigns.isError && <span style={{ fontSize: 12, color: 'var(--danger, #b42318)' }}>Не удалось загрузить кампании.</span>}
            {reachAllCampaigns.data?.length ? (
              <div className="reach-campaign-list__items">
                {reachAllCampaigns.data.map((campaign) => (
                  <div key={campaign.id} className={`reach-campaign-row${reachResult?.id === campaign.id ? ' is-selected' : ''}`}>
                    <button
                      type="button"
                      className="reach-campaign-row__open"
                      onClick={() => {
                        setReachCreateOpen(false)
                        setReachProjectId(campaign.project_id)
                        setReachResult(campaign)
                        setReachDetailTab('recipients')
                      }}
                    >
                      <span className="reach-campaign-row__name">{campaign.name || `Кампания #${campaign.id}`}</span>
                      <span className="reach-campaign-row__meta">
                        <span className="reach-campaign-row__summary">
                          {projectOptions(projects).find((option) => option.value === String(campaign.project_id))?.label?.split('·')[0]?.trim() ?? `Проект ${campaign.project_id}`}
                          {' · '}
                          {campaign.recipient_count} получателей
                        </span>
                        <span className={`reach-status reach-status--${campaign.status || 'unknown'}`}>{reachCampaignStatusLabel(campaign.status)}</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="reach-campaign-row__delete"
                      aria-label={`Удалить кампанию ${campaign.name || campaign.id}`}
                      title="Удалить кампанию"
                      disabled={deleteReachCampaign.isPending}
                      onClick={() => {
                        if (window.confirm(`Удалить кампанию «${campaign.name || `#${campaign.id}`}» вместе с получателями, зафиксированным сообщением и историей тестов?`)) deleteReachCampaign.mutate(campaign.id)
                      }}
                    >
                      <span aria-hidden="true">×</span><span className="sr-only">Удалить</span>
                    </button>
                  </div>
                ))}
              </div>
            ) : <div className="reach-campaign-list__empty">{reachProjectId == null ? 'Сначала выберите проект.' : <>Кампаний пока нет.<br />Создайте первую, чтобы подготовить тест.</>}</div>}
            {deleteReachCampaign.isError && <span style={{ fontSize: 12, color: 'var(--danger, #b42318)' }}>Не удалось удалить кампанию: {deleteReachCampaign.error instanceof Error ? deleteReachCampaign.error.message : 'ошибка'}</span>}
          </aside>
          <main className="reach-main">
          {reachCreateOpen && (
            <div className="reach-create-card">
              <div className="reach-create-card__header">
                <div><p className="reach-section__eyebrow">Новая кампания</p><b>Настройте письмо и аудиторию</b></div>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setReachCreateOpen(false)}>Отмена</button>
              </div>
          <div className="reach-scope-note">
            <b>Проект:</b> {projectOptions(projects).find((option) => option.value === String(reachProjectId))?.label ?? 'не выбран'}
            <span>Варианты сообщений ниже принадлежат только выбранному проекту.</span>
          </div>
          <label style={{ display: 'grid', gap: 5, fontSize: 13 }}>
            Название кампании
            <input
              value={reachName}
              onChange={(event) => setReachName(event.target.value)}
              maxLength={255}
              placeholder="Например: кофе — закупщики"
              style={{ padding: '9px 11px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)' }}
            />
          </label>
          <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
            <span>Категории аудитории (пусто = все)</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {VERDICT_OPTIONS.map((option) => {
                const active = reachVerdicts.includes(option.value)
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`aud-verdict-chip aud-verdict-chip--${option.value}${active ? ' is-on' : ''}`}
                    aria-pressed={active}
                    onClick={() => setReachVerdicts((current) => active
                      ? current.filter((value) => value !== option.value)
                      : [...current, option.value])}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>
          </div>
          {reachTemplateSavedNotice && (
            <div style={{ padding: '9px 11px', borderRadius: 8, background: 'var(--surface)', border: '1px solid var(--line)', fontSize: 13 }}>
              {reachTemplateSavedNotice}
            </div>
          )}
          <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
            <span>Варианты сообщений</span>
            {reachAvailability?.templates.length ? reachAvailability.templates.map((template) => {
              const active = reachTemplateIds.includes(template.id)
              const stageLabel = template.stage === 'permission' ? 'Первое: запрос разрешения' : 'Второе: предложение после согласия'
              return (
                <label key={template.id} style={{ display: 'flex', gap: 8, alignItems: 'start', padding: '7px 9px', borderRadius: 8, background: 'var(--surface)' }}>
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => setReachTemplateIds((current) => active
                      ? current.filter((id) => id !== template.id)
                      : [...current, template.id])}
                  />
                  <span><b>{template.tag}</b> <small style={{ color: 'var(--muted)' }}>· {stageLabel}</small> — {template.text.length > 180 ? `${template.text.slice(0, 180)}…` : template.text}</span>
                </label>
              )
            }) : <span>Шаблонов пока нет. Добавьте и выберите хотя бы один вариант письма.</span>}
          </div>
          <details className="reach-template-create">
            <summary className="btn btn--ghost btn--sm">+ Добавить вариант письма</summary>
            <div className="reach-template-create__body">
            <input
              value={reachTemplateTag}
              onChange={(event) => setReachTemplateTag(event.target.value)}
              maxLength={100}
              placeholder="Тег варианта, например coffee-1"
              style={{ padding: '9px 11px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)' }}
            />
            <label style={{ display: 'grid', gap: 5, color: 'var(--muted)', fontSize: 12 }}>
              Этап диалога
              <select
                value={reachTemplateStage}
                onChange={(event) => setReachTemplateStage(event.target.value as 'permission' | 'offer')}
                style={{ padding: '9px 11px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)' }}
              >
                <option value="permission">Первое сообщение — спросить разрешение</option>
                <option value="offer">Второе сообщение — предложение после согласия</option>
              </select>
            </label>
            <textarea
              value={reachTemplateText}
              onChange={(event) => setReachTemplateText(event.target.value)}
              maxLength={4000}
              rows={5}
              placeholder={reachTemplateStage === 'permission' ? 'Короткое первое сообщение с контекстом и вопросом' : 'Короткое предложение — только после согласия получателя'}
              style={{ padding: '9px 11px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', resize: 'vertical' }}
            />
            <input
              type="file"
              accept="image/*"
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (!file) return
                if (!file.type.startsWith('image/') || file.size > 5 * 1024 * 1024) {
                  event.target.value = ''
                  return
                }
                const reader = new FileReader()
                reader.onload = () => setReachTemplateMedia({ data: String(reader.result), mime: file.type })
                reader.readAsDataURL(file)
              }}
            />
            {reachTemplateMedia && <img src={reachTemplateMedia.data} alt="Превью вложения" style={{ maxWidth: 180, maxHeight: 120, borderRadius: 8 }} />}
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={!reachTemplateTag.trim() || !reachTemplateText.trim() || createReachTemplate.isPending}
              onClick={() => createReachTemplate.mutate()}
            >
              {createReachTemplate.isPending ? 'Сохраняю…' : 'Сохранить вариант'}
            </button>
            {createReachTemplate.isError && <span style={{ color: 'var(--danger, #b42318)' }}>{createReachTemplate.error instanceof Error ? createReachTemplate.error.message : 'Не удалось сохранить вариант'}</span>}
            </div>
          </details>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn--ai btn--sm"
              disabled={!reachProjectId || !reachName.trim() || !reachTemplateIds.length || createReach.isPending}
              onClick={() => createReach.mutate()}
            >
              {createReach.isPending ? 'Сохраняю…' : 'Создать кампанию'}
            </button>
            {!reachProjectId && <span style={{ alignSelf: 'center', fontSize: 13 }}>Выберите проект</span>}
            {!!reachProjectId && !reachTemplateIds.length && <span style={{ alignSelf: 'center', fontSize: 13 }}>Добавьте и выберите хотя бы один вариант письма</span>}
            {createReach.isError && <span style={{ alignSelf: 'center', fontSize: 13, color: 'var(--danger, #b42318)' }}>Не удалось создать кампанию: {createReach.error instanceof Error ? createReach.error.message : 'проверьте проект и варианты'}</span>}
          </div>
            </div>
          )}
          {reachResult && (
            <div className="reach-detail">
              <header className="reach-detail__header">
                <div>
                  <p className="reach-section__eyebrow">Кампания #{reachResult.id}</p>
                  <h3>{reachResult.name}</h3>
                  <p>{reachResult.candidate_count ?? reachResult.recipient_count} получателей</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className={`reach-status reach-status--${reachResult.status || 'unknown'}`}>{reachCampaignStatusLabel(reachResult.status)}</span>
                  {reachResult.status === 'draft' && (
                    <>
                      <button type="button" className="btn btn--ghost btn--sm" disabled={refreshReachQueue.isPending} onClick={() => refreshReachQueue.mutate(reachResult.id)}>
                        {refreshReachQueue.isPending ? 'Обновляю…' : 'Обновить очередь'}
                      </button>
                      <button type="button" className="btn btn--ai btn--sm" disabled={preflightReach.isPending} onClick={() => preflightReach.mutate(reachResult.id)}>
                        {preflightReach.isPending ? 'Проверяю…' : 'Запустить рассылку'}
                      </button>
                    </>
                  )}
                  {reachResult.status === 'ready' && (
                    <>
                      <button type="button" className="btn btn--ghost btn--sm" disabled={pauseReach.isPending} onClick={() => pauseReach.mutate(reachResult.id)}>
                        {pauseReach.isPending ? 'Ставлю на паузу…' : 'Пауза'}
                      </button>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>Рассылка активна. Для пересборки очереди сначала поставьте её на паузу.</span>
                    </>
                  )}
                  {reachResult.status === 'paused' && (
                    <>
                      <button type="button" className="btn btn--ghost btn--sm" disabled={refreshReachQueue.isPending} onClick={() => refreshReachQueue.mutate(reachResult.id)}>
                        {refreshReachQueue.isPending ? 'Обновляю…' : 'Обновить очередь'}
                      </button>
                      <button type="button" className="btn btn--ai btn--sm" disabled={preflightReach.isPending} onClick={() => preflightReach.mutate(reachResult.id)}>
                        {preflightReach.isPending ? 'Проверяю…' : 'Продолжить'}
                      </button>
                    </>
                  )}
                </div>
              </header>
              {reachQueueRefreshNotice && <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: 'var(--surface)', border: '1px solid var(--line)', fontSize: 12 }}>{reachQueueRefreshNotice}</div>}
              {reachQueueError && <div style={{ marginTop: 10, color: 'var(--danger, #b42318)', fontSize: 12 }}>Не удалось обновить очередь: {reachQueueError instanceof Error ? reachQueueError.message : 'ошибка'}</div>}
              <nav className="reach-detail__tabs" aria-label="Разделы кампании">
                {([
                  ['recipients', 'Получатели'],
                  ['dialogs', 'Диалоги'],
                  ['message', 'Сообщение'],
                ] as Array<[typeof reachDetailTab, string]>).map(([tab, label]) => (
                  <button key={tab} type="button" className={reachDetailTab === tab ? 'is-active' : undefined} onClick={() => setReachDetailTab(tab)}>{label}</button>
                ))}
              </nav>
              {reachDetailTab === 'dialogs' && reachProjectId != null && (
                <ReachWorkflow
                  projectId={reachProjectId}
                  projectName={projectOptions(projects).find((option) => option.value === String(reachProjectId))?.label ?? 'выбранный проект'}
                />
              )}
              {reachDetailTab === 'recipients' && <>
              <details className="reach-addressability">
                <summary>Адресуемость и ограничения</summary>
                <div className="reach-addressability__content">
              {(() => {
                const breakdown = reachAddressability(reachResult) ?? (reachDryRun.data as ReachAddressabilitySummaryResponse | undefined)
                const withUsername = addressabilityCount(breakdown, 'with_username', 'username', 'username_count')
                const withAccessHash = addressabilityCount(breakdown, 'with_id_access_hash', 'id_access_hash', 'id_access_hash_count')
                const withoutAddress = addressabilityCount(breakdown, 'without_address', 'no_address', 'no_address_count')
                const dmNotChecked = addressabilityCount(breakdown, 'dm_not_checked', 'unverified_dm', 'not_checked', 'dm_unchecked_count')
                if (!breakdown) {
                  return <div style={{ fontSize: 12, color: 'var(--muted)' }}>Разбиение адресуемости появится после dry-run предпроверки.</div>
                }
                return (
                  <div style={{ display: 'grid', gap: 5, fontSize: 12 }}>
                    <b>Dry-run адресуемости (не результат доставки)</b>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                      <span>с @username: <b>{withUsername ?? '—'}</b></span>
                      <span>ID + access_hash: <b>{withAccessHash ?? '—'}</b></span>
                      <span>без адреса: <b>{withoutAddress ?? '—'}</b></span>
                      <span>Разрешение DM не проверено: <b>{dmNotChecked ?? '—'}</b></span>
                    </div>
                    <span style={{ color: 'var(--muted)' }}>Telegram privacy и доступность личного чата здесь не проверяются.</span>
                  </div>
                )
              })()}
                </div>
              </details>
              {reachResult.status === 'draft' && (
                <>
                  <div style={{ display: 'grid', gap: 7 }}>
                    <textarea
                      value={reachManualRecipient}
                      onChange={(event) => {
                        setReachManualRecipient(event.target.value)
                        setReachManualAddSummary(null)
                      }}
                      placeholder={'@username или Telegram ID\nМожно вставить несколько: через запятую, пробел или с новой строки'}
                      rows={3}
                      style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', resize: 'vertical', minWidth: 320 }}
                    />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button type="button" className="btn btn--ghost btn--sm" disabled={!pastedManualRecipients.length || addReachManual.isPending} onClick={() => addReachManual.mutate()}>
                        {addReachManual.isPending ? 'Добавляю…' : `Добавить адреса${pastedManualRecipients.length ? ` (${pastedManualRecipients.length})` : ''}`}
                      </button>
                      <button type="button" className="btn btn--ghost btn--sm" disabled={!reachDeliveries.data?.length || deleteAllReach.isPending} onClick={() => deleteAllReach.mutate()}>Удалить всех</button>
                      {reachManualRecipient.trim() && !pastedManualRecipients.length && <span style={{ color: 'var(--danger, #b42318)', fontSize: 12 }}>Укажите @username или Telegram ID.</span>}
                    </div>
                    {addReachManual.isError && <span style={{ color: 'var(--danger, #b42318)', fontSize: 12 }}>Не удалось добавить адреса: {addReachManual.error instanceof Error ? addReachManual.error.message : 'ошибка'}</span>}
                    {reachManualAddSummary && <span style={{ color: 'var(--muted)', fontSize: 12 }}>{reachManualAddSummary}</span>}
                  </div>
                  <label style={{ display: 'flex', gap: 7, alignItems: 'center' }}><input type="checkbox" checked={reachReviewed} onChange={(event) => setReachReviewed(event.target.checked)} /> Я проверил(а) текст и список кандидатов</label>
                </>
              )}
              {reachResult.status === 'ready' && (
                <div style={{ padding: '8px 10px', borderRadius: 8, fontSize: 12, color: 'var(--muted)' }}>
                  Очередь изменяется только на паузе. Отправленные и уже обработанные контакты сохраняются в истории.
                </div>
              )}
              {reachResult.status === 'paused' && <div style={{ padding: '8px 10px', borderRadius: 8, fontSize: 12, color: 'var(--muted)' }}>Рассылка на паузе. «Обновить очередь» добавит новых подходящих участников и пересчитает время только для неотправленных.</div>}
              <div className="reach-detail__section">
                <h3 style={{ margin: 0, fontSize: 15 }}>Получатели</h3>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                  Получатели кампании: {reachDeliveries.data?.length ?? reachResult.recipient_count}.
                  {reachResult.status === 'draft' ? ` Вручную добавлено: ${(reachDeliveries.data ?? []).filter(isManualDelivery).length}. Telegram DM ещё не проверен.` : ' История отправки сохраняется; пересобираются только ожидающие контакты.'}
                </div>
                {reachDeliveries.isLoading && <span style={{ fontSize: 12, color: 'var(--muted)' }}>Загружаю получателей…</span>}
                {reachDeliveries.isError && <span style={{ fontSize: 12, color: 'var(--danger, #b42318)' }}>Не удалось загрузить получателей: {reachDeliveries.error instanceof Error ? reachDeliveries.error.message : 'ошибка'}</span>}
                <div className="reach-recipient-sort-note">Сначала самые перспективные: горячие → перспективные → мало данных → не подходят. Внутри категории — по баллу.</div>
                <div className="reach-recipient-list">
                  {reachSortedDeliveries.map((delivery) => {
                    const recipient = delivery.display_name || (delivery.username ? `@${delivery.username}` : delivery.manual_username ? `@${delivery.manual_username}` : `ID ${delivery.telegram_user_id ?? '—'}`)
                    const senderAccount = delivery.sandbox_sender_account_id == null
                      ? null
                      : reachSenderAccounts.data?.find((account) => account.id === delivery.sandbox_sender_account_id)
                    const sender = senderAccount
                      ? `${senderAccount.display_name || 'Без имени'}${senderAccount.username ? ` @${senderAccount.username.replace(/^@+/, '')}` : ''}`
                      : delivery.sandbox_sender_account_id != null ? `аккаунт #${delivery.sandbox_sender_account_id}` : null
                    const scheduledAt = formatDateTime(delivery.scheduled_at)
                    const sentAt = formatDateTime(delivery.sent_at)
                    return (
                      <details key={delivery.id} className="reach-recipient-row">
                        <summary className="reach-recipient-row__summary">
                          <span><b>{recipient}</b>{delivery.score != null ? ` · ${delivery.score}/100` : ''} · <span className={`aud-verdict aud-verdict--${delivery.verdict || 'insufficient_data'}`}>{reachVerdictLabel(delivery.verdict)}</span></span>
                          <span className="reach-recipient-row__summary-meta">{reachDeliveryStatusLabel(delivery.status)} · подробнее</span>
                        </summary>
                        <div className="reach-recipient-row__details">
                          <div style={{ display: 'grid', gap: 4 }}>
                            <span title="Адресуемость не гарантирует доставку">{deliveryAddressLabel(delivery.addressability_status ?? delivery.addressability ?? (delivery.has_access_hash ? 'id_access_hash' : undefined))} · {deliveryDmLabel(delivery.dm_status)}</span>
                            {delivery.bio && <span><b>Bio:</b> {delivery.bio}</span>}
                            {delivery.portrait && <span><b>Портрет:</b> {delivery.portrait}</span>}
                            {delivery.reason && <span><b>Обоснование:</b> {delivery.reason}</span>}
                            {delivery.messages_count != null && <span><b>Сообщений в источниках:</b> {delivery.messages_count}</span>}
                            {delivery.last_message_at && <span><b>Последняя активность:</b> {formatDateTime(delivery.last_message_at)}</span>}
                            {(delivery.interest_score != null || delivery.client_fit_score != null || delivery.recency_score != null || delivery.noise_score != null) && (
                              <span><b>Профиль оценки:</b> интерес {delivery.interest_score ?? '—'}, fit {delivery.client_fit_score ?? '—'}, свежесть {delivery.recency_score ?? '—'}, шум {delivery.noise_score ?? '—'}</span>
                            )}
                            {delivery.evidence?.length ? <div><b>Основания:</b>{delivery.evidence.map((item, index) => <div key={`${delivery.id}-evidence-${index}`} className="reach-recipient-row__evidence">«{item.quote || '—'}»</div>)}</div> : null}
                          </div>
                          <span style={{ fontSize: 12, color: delivery.status === 'failed' ? 'var(--danger, #b42318)' : 'var(--muted)' }}>{reachDeliveryStatusLabel(delivery.status)}{delivery.failure_reason ? ` · ${delivery.failure_reason}` : ''}</span>
                          {scheduledAt && <span style={{ fontSize: 12, color: 'var(--muted)' }}>В очереди: {scheduledAt}.</span>}
                          {(sender || delivery.sandbox_telegram_message_id != null || delivery.sandbox_telegram_delivery_status || sentAt || delivery.template_tag) && (
                            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                              {delivery.template_tag ? `Вариант: ${delivery.template_tag}. ` : ''}
                              {sender ? `Отправитель: ${sender}. ` : ''}
                              {delivery.sandbox_telegram_message_id != null ? `Telegram №${delivery.sandbox_telegram_message_id}. ` : ''}
                              {delivery.sandbox_telegram_accepted != null ? sandboxTelegramAcceptanceLabel(delivery.sandbox_telegram_accepted, delivery.sandbox_telegram_message_id) + '. ' : ''}
                              {delivery.sandbox_telegram_delivery_status ? `Статус Telegram: ${sandboxReadLabel(delivery.sandbox_telegram_delivery_status, Boolean(delivery.sandbox_telegram_status_checked_at))}. ` : ''}
                              {sentAt ? `Время отправки: ${sentAt}.` : ''}
                            </span>
                          )}
                          {delivery.sandbox_detail && <span style={{ fontSize: 12, color: 'var(--muted)' }}>{delivery.sandbox_detail}</span>}
                          {reachResult.status === 'draft'
                            ? <button type="button" className="btn btn--ghost btn--sm" onClick={() => deleteReach.mutate(delivery.id)}>Удалить</button>
                            : <span style={{ fontSize: 12, color: 'var(--muted)' }}>Только просмотр</span>}
                        </div>
                      </details>
                    )
                  })}
                </div>
              </div>
              </>}
              {reachDetailTab === 'message' && <section className="reach-detail__section reach-message">
                <h3 style={{ margin: 0, fontSize: 15 }}>Варианты сообщений</h3>
                <div className="reach-message__note">Шаблоны для проекта. Первые — рассылаются автоматически. При ответе — эскалация на менеджера.</div>
                {reachAvailability?.templates.length ? (() => {
                  const campaignTemplateIds = reachResult?.template_ids?.length ? new Set(reachResult.template_ids) : null
                  const campaignTemplates = campaignTemplateIds
                    ? reachAvailability.templates.filter((t) => campaignTemplateIds.has(t.id))
                    : reachAvailability.templates
                  if (!campaignTemplates.length) {
                    return <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 8 }}>В этой кампании нет вариантов сообщений.</div>
                  }
                  const permissions = campaignTemplates.filter((t) => t.stage === 'permission')
                  const offers = campaignTemplates.filter((t) => t.stage === 'offer')
                  return (
                    <div style={{ display: 'grid', gap: 12, marginTop: 8 }}>
                      {permissions.length > 0 && (
                        <div>
                          <h4 style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--muted)' }}>Первые сообщения ({permissions.length})</h4>
                          {permissions.map((t) => (
                            <div key={t.id} style={{ padding: '8px 10px', borderRadius: 8, background: 'var(--surface)', border: '1px solid var(--line)', marginBottom: 6 }}>
                              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}><b>{t.tag}</b></div>
                              <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{t.text}</div>
                              {t.media_data && <img src={t.media_data.startsWith('data:') ? t.media_data : `data:${t.media_mime_type};base64,${t.media_data}`} alt="Вложение" style={{ maxWidth: 200, maxHeight: 140, marginTop: 6, borderRadius: 8 }} />}
                            </div>
                          ))}
                        </div>
                      )}
                      {offers.length > 0 && (
                        <div>
                          <h4 style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--muted)' }}>Вторые сообщения ({offers.length})</h4>
                          {offers.map((t) => (
                            <div key={t.id} style={{ padding: '8px 10px', borderRadius: 8, background: 'var(--surface)', border: '1px solid var(--line)', marginBottom: 6 }}>
                              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}><b>{t.tag}</b></div>
                              <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{t.text}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })() : <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 8 }}>Шаблоны не найдены для этого проекта.</div>}
              </section>}
              {reachDetailTab === 'test' && (() => {
                if (reachResult.status !== 'draft') return null
                const manualDeliveries = (reachDeliveries.data ?? []).filter(isManualDelivery)
                if (!manualDeliveries.length) return null
                const tooMany = manualDeliveries.length > 3
                return (
                  <div className="reach-test-card">
                    <div>
                      <b>🧪 Тестовая отправка</b>
                      <div style={{ marginTop: 4, fontSize: 12, color: 'var(--muted)' }}>
                        Тест идёт только по адресам, добавленным вручную. Можно проверить до трёх своих аккаунтов; остальные получатели кампании в тест не попадут.
                      </div>
                    </div>
                    <div style={{ fontSize: 12 }}>
                      Выбрано вручную: <b>{manualDeliveries.length}</b> из 3 максимум. В тест попадут только эти адресаты, остальные кандидаты кампании будут проигнорированы.
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      Система сама выберет следующий подходящий sender-аккаунт и распределит тестовые сообщения безопасно. Выбирать аккаунт вручную не нужно.
                    </div>
                    {tooMany && <div style={{ color: 'var(--danger, #b42318)', fontSize: 12 }}>Для теста оставьте не более трёх ручных получателей.</div>}
                    <label style={{ display: 'flex', gap: 7, alignItems: 'start', fontSize: 12 }}>
                      <input type="checkbox" checked={reachSandboxReviewed} onChange={(event) => setReachSandboxReviewed(event.target.checked)} />
                      <span>Я понимаю, что это ограниченная тестовая отправка только моим аккаунтам, а не запуск массовой рассылки.</span>
                    </label>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={tooMany || !reachReviewed || !reachSandboxReviewed || runReachSandbox.isPending || reachResult.status !== 'draft'}
                      onClick={() => runReachSandbox.mutate()}
                    >
                      {runReachSandbox.isPending ? 'Проверяю…' : 'Проверить тест (без отправки)'}
                    </button>
                    {runReachSandbox.isError && (
                      <div style={{ color: 'var(--danger, #b42318)', fontSize: 12 }}>
                        Ошибка dry-run: {runReachSandbox.error instanceof Error ? runReachSandbox.error.message : 'не удалось выполнить проверку'}
                      </div>
                    )}
                    {reachSandboxResult && (
                      <div style={{ display: 'grid', gap: 5, fontSize: 12 }}>
                        <b>Статус: {sandboxStatusLabel(reachSandboxResult.status)} · режим: {reachSandboxResult.mode}</b>
                        {reachSandboxResult.blocked_reason && <span style={{ color: 'var(--danger, #b42318)' }}>{reachSandboxResult.blocked_reason}</span>}
                        {(reachSandboxResult.recipients ?? []).map((recipient) => (
                          <div key={recipient.delivery_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, borderTop: '1px solid var(--line)', paddingTop: 5 }}>
                            <span>{recipient.target}</span>
                            <span title={recipient.error || undefined}>{sandboxStatusLabel(recipient.status)}{recipient.error ? ` · ${recipient.error}` : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      disabled={tooMany || !reachReviewed || !reachSandboxReviewed || sendReachSandbox.isPending || !!reachSendResult}
                      onClick={() => sendReachSandbox.mutate()}
                    >
                      {sendReachSandbox.isPending ? 'Отправляю…' : 'Отправить тестовые сообщения (до 3)'}
                    </button>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>Пауза между адресатами: 120–180 секунд. При FloodWait, блокировке или запрете личных сообщений отправка фиксируется и останавливается.</div>
                    {sendReachSandbox.isError && <div style={{ color: 'var(--danger, #b42318)', fontSize: 12 }}>Ошибка тестовой отправки: {sendReachSandbox.error instanceof Error ? sendReachSandbox.error.message : 'не удалось выполнить отправку'}</div>}
                    {reachSendResult && (
                      <div style={{ display: 'grid', gap: 8, fontSize: 12 }}>
                        <b>Тестовая отправка: {sandboxStatusLabel(reachSendResult.status)}</b>
                        {reachSendResult.error && <span style={{ color: 'var(--danger, #b42318)' }}>{reachSendResult.error}</span>}
                        {reachSendResult.results.map((result) => {
                          const sender = result.sender_account_id == null
                            ? null
                            : reachSenderAccounts.data?.find((account) => account.id === result.sender_account_id)
                          const senderLabel = sender
                            ? `${sender.display_name || 'Без имени'}${sender.username ? ` @${sender.username.replace(/^@+/, '')}` : ''}`
                            : result.sender_account_id != null ? `аккаунт #${result.sender_account_id}` : null
                          return <div key={result.id} style={{ borderTop: '1px solid var(--line)', paddingTop: 5 }}>
                            {result.reach_delivery_id}: {sandboxStatusLabel(result.status)}{senderLabel ? ` · отправитель: ${senderLabel}` : ''}{result.detail ? ` · ${result.detail}` : ''}
                          </div>
                        })}
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={checkReachSandboxStatus.isPending}
                          onClick={() => checkReachSandboxStatus.mutate()}
                        >
                          {checkReachSandboxStatus.isPending ? 'Проверяю статус…' : 'Проверить статус'}
                        </button>
                        {checkReachSandboxStatus.isError && <span style={{ color: 'var(--danger, #b42318)' }}>Не удалось проверить статус: {checkReachSandboxStatus.error instanceof Error ? checkReachSandboxStatus.error.message : 'ошибка'}</span>}
                        {reachStatusResult && (
                          <div style={{ display: 'grid', gap: 6, padding: 9, border: '1px solid var(--line)', borderRadius: 8 }}>
                            <b>Статус Telegram{formatDateTime(reachStatusResult.checked_at) ? ` · проверено ${formatDateTime(reachStatusResult.checked_at)}` : ''}</b>
                            {reachStatusResult.results.map((result) => {
                              const delivery = reachDeliveries.data?.find((item) => item.id === result.reach_delivery_id)
                              const target = delivery?.manual_username
                                ? `@${delivery.manual_username.replace(/^@+/, '')}`
                                : delivery?.manual_telegram_user_id != null
                                  ? `ID ${delivery.manual_telegram_user_id}`
                                  : `Получатель #${result.reach_delivery_id}`
                              const checkedAt = formatDateTime(result.telegram_status_checked_at)
                              return (
                                <div key={result.id} style={{ display: 'grid', gap: 3, borderTop: '1px solid var(--line)', paddingTop: 6 }}>
                                  <b>{target}</b>
                                  <span>{result.status === 'sent' ? 'Отправлено' : `Не отправлено: ${sandboxStatusLabel(result.status)}`}</span>
                                  <span>{sandboxTelegramAcceptanceLabel(result.telegram_accepted, result.telegram_message_id)}</span>
                                  <span>{sandboxDeliveryEvidenceLabel(result.telegram_delivery_status)}</span>
                                  <span>{sandboxReadLabel(result.telegram_delivery_status, Boolean(result.telegram_status_checked_at))}</span>
                                  <span>{checkedAt ? `Проверено: ${checkedAt}` : 'Время проверки неизвестно.'}</span>
                                  {result.detail && <span style={{ color: 'var(--muted)' }}>{result.detail}</span>}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })()}
                  {reachDetailTab === 'test' && reachResult.status === 'draft' && reachReviewed && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  style={{ marginLeft: 8 }}
                  disabled={preflightReach.isPending}
                  onClick={() => preflightReach.mutate(reachResult.id)}
                >
                    {preflightReach.isPending ? 'Проверяю…' : 'Проверить готовность (без отправки)'}
                </button>
              )}
            </div>
          )}
          {!reachResult && !reachCreateOpen && !reachAllCampaigns.isLoading && (
            <div className="reach-main__empty">
              <span aria-hidden="true">✦</span>
              <b>Выберите кампанию</b>
              <p>Откройте её из списка слева или создайте новую.</p>
            </div>
          )}
          </main>
          </div>
          {hybridModalOpen && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setHybridModalOpen(false)}>
              <div style={{ background: 'var(--surface)', borderRadius: 18, padding: 24, maxWidth: 520, width: '90%', maxHeight: '80vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 12px' }}>🔀 Гибридная кампания</h3>
                <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 16px' }}>Выберите кампании для объединения. Система найдет пересечения, удалит дубли из исходных кампаний и сгенерирует гибридные сообщения.</p>
                <label style={{ display: 'grid', gap: 5, fontSize: 13 }}>
                  Название
                  <input value={hybridName} onChange={(e) => setHybridName(e.target.value)} style={{ padding: '9px 11px', borderRadius: 8, border: '1px solid var(--line)' }} />
                </label>
                <div style={{ display: 'grid', gap: 6, fontSize: 13, marginTop: 12 }}>
                  <span>Выберите кампании ({hybridSelected.length} выбрано)</span>
                  {(reachAllCampaigns.data ?? []).map((c) => (
                    <label key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '7px 9px', borderRadius: 8, background: 'var(--surface)', border: '1px solid var(--line)' }}>
                      <input
                        type="checkbox"
                        checked={hybridSelected.includes(c.id)}
                        onChange={() => setHybridSelected((cur) => cur.includes(c.id) ? cur.filter((id) => id !== c.id) : [...cur, c.id])}
                      />
                      <span>{c.name} · {c.recipient_count} получателей</span>
                    </label>
                  ))}
                </div>
                <label style={{ display: 'grid', gap: 5, fontSize: 13, marginTop: 12 }}>
                  Количество вариаций сообщений
                  <input type="number" min={1} max={30} value={hybridVariations} onChange={(e) => setHybridVariations(Number(e.target.value) || 10)} style={{ padding: '9px 11px', borderRadius: 8, border: '1px solid var(--line)' }} />
                </label>
                {hybridResult && (
                  <div style={{ padding: '12px', borderRadius: 8, background: 'var(--accent-soft)', fontSize: 13, marginTop: 12 }}>
                    ✓ Создана кампания #{hybridResult.campaign_id} с {hybridResult.hybrid_recipient_count} получателями.
                    {Object.entries(hybridResult.moved_from_campaigns).map(([cid, count]) => <div key={cid} style={{ fontSize: 12, color: 'var(--muted)' }}>Из кампании #{cid}: удалено {count}</div>)}
                  </div>
                )}
                {createHybrid.isError && <p style={{ color: 'var(--danger, #b42318)', fontSize: 13, marginTop: 8 }}>{createHybrid.error instanceof Error ? createHybrid.error.message : 'Ошибка создания'}</p>}
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button type="button" className="btn btn--ai btn--sm" disabled={hybridSelected.length < 2 || createHybrid.isPending} onClick={() => createHybrid.mutate()}>
                    {createHybrid.isPending ? 'Создаю…' : 'Создать гибрид'}
                  </button>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setHybridModalOpen(false); setHybridResult(null); setHybridSelected([]) }}>Закрыть</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <SummaryStrip
        key={`aud-summary-${summaryKey}`}
        summary={summary}
        activeVerdicts={verdicts}
        onSelect={(value) => {
          setSelectedId(null)
          if (value === '') {
            setVerdicts(new Set())
            return
          }
          setVerdicts((prev) => {
            const next = new Set(prev)
            if (next.has(value as VerdictFilter)) next.delete(value as VerdictFilter)
            else next.add(value as VerdictFilter)
            return next
          })
        }}
      />

      {!jobRunning && pendingLabel !== null && (
        <div className="aud-score-pending">
          <div>
            <b>{pendingLabel}</b>
            <span>
              {!pendingCount
                ? 'Все текущие оценки актуальны. Новые сообщения или новые участники будут обработаны при следующем запуске.'
                : scoreVerdicts.length
                  ? `AI проверит участников из категорий: ${scoreVerdicts.map((v) => VERDICT_LABEL[v]).join(', ')}. Уже актуальные оценки повторно не списываются.`
                  : 'Выбрана только категория «Мало данных»: она не переоценивается.'}
            </span>
          </div>
          <button
            type="button"
            className="btn btn--ai btn--sm"
            onClick={() => startScore.mutate()}
            disabled={!canStartScore || jobRunning || startScore.isPending || !projectId}
            title={
              canStartScore
                ? (verdictList.length
                  ? `Переоценить: ${scoreVerdicts.join(', ')}`
                  : 'Выберите «Горячие», «Перспективные» или «Не подходят»')
                : 'Нет новых данных для оценки'
            }
          >
            {startScore.isPending ? 'Запускаю...' : startScoreLabel}
          </button>
        </div>
      )}

      {jobRunning && activeJob && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            marginBottom: 4,
            background: 'var(--accent-soft)',
            border: '1px solid color-mix(in oklab, var(--accent) 30%, transparent)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
            color: 'var(--accent-ink)',
          }}
        >
          <span
            className="animate-spin"
            style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              border: '2px solid currentColor',
              borderTopColor: 'transparent',
              display: 'inline-block',
            }}
          />
          {/* Keyed so React remounts the text node on every progress tick —
              it does not reliably patch a changing text node in place here
              (same quirk handled on the buttons and in DashboardPage). */}
          <span
            key={`${(activeJob.result as { phase?: string } | null)?.phase ?? 'x'}-${activeJob.status}-${activeJob.progress_done}-${activeJob.progress_total}`}
          >
            {activeJob.kind === 'member_snapshot' ||
            (activeJob.kind === 'score' &&
              (activeJob.result as { phase?: string } | null)?.phase === 'collect')
              ? 'Сбор всех участников Telegram'
              : activeJob.kind === 'collect_score' &&
            (activeJob.result as { phase?: string } | null)?.phase === 'collect'
              ? 'Сбор истории участников'
              : 'AI-оценка участников'}
            {activeJob.progress_total > 0
              ? `: ${activeJob.progress_done}/${activeJob.progress_total}`
              : ' запускается…'}
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={cancelJob.isPending}
            onClick={() => cancelJob.mutate()}
            title="Остановить после текущего источника"
            style={{ marginLeft: 'auto' }}
          >
            {cancelJob.isPending ? 'Останавливаю…' : 'Стоп'}
          </button>
        </div>
      )}
      {activeJob?.status === 'canceled' && (
        <div className="aud-job-note aud-job-note--muted aud-job-note--action">
          <span>
            {activeJob.kind === 'member_snapshot'
              ? 'Сбор участников остановлен. Уже обработанные источники сохранены.'
              : 'Оценка остановлена. Уже завершённые участники сохранены.'}
            {activeJob.kind !== 'member_snapshot' && pendingCount > 0
              ? ` Повторный запуск обработает выбранные категории: ${pendingCount}.`
              : ''}
          </span>
        </div>
      )}
      {activeJob?.kind === 'member_snapshot' && activeJob.status === 'done' && (
        <div className="aud-job-note aud-job-note--muted aud-job-note--action">
          <span>
            Сбор участников завершён.
            {activeJob.result && typeof activeJob.result.added === 'number'
              ? ` Добавлено: ${activeJob.result.added}. Обновлено: ${activeJob.result.updated ?? 0}.`
              : ''}
            {activeJob.result && typeof activeJob.result.failed_sources === 'number' && activeJob.result.failed_sources > 0
              ? ` Не удалось обработать источников: ${activeJob.result.failed_sources}.`
              : ''}
          </span>
        </div>
      )}
      {activeJob?.status === 'failed' && (
        <div
          style={{
            padding: '10px 14px',
            marginBottom: 4,
            background: 'var(--danger-soft)',
            border: '1px solid var(--danger-line)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
            color: 'var(--danger-ink)',
          }}
        >
          {activeJob.kind === 'member_snapshot' ? 'Сбор участников' : 'Оценка'} не удался: {activeJob.error || 'неизвестная ошибка'}
        </div>
      )}

      {!projectId && !projectsLoading && (
        <div className="aud-empty">Нет проектов</div>
      )}

      {projectId && (
        <div ref={gridRef} className="aud-grid" style={gridStyle}>
          <div className="aud-table-wrap">
            {scoresLoading && <div className="aud-empty">Загрузка...</div>}
            {!scoresLoading && (scores?.items.length ?? 0) === 0 && (
              <div className="aud-empty">Участников пока нет</div>
            )}
            {(scores?.items.length ?? 0) > 0 && (
              <table className="aud-table">
                <thead>
                  <tr>
                    <th>Участник</th>
                    <th>Вердикт</th>
                    <th>Балл</th>
                    <th>Сообщ.</th>
                    <th>Активность</th>
                  </tr>
                </thead>
                <tbody>
                  {scores?.items.map((member) => (
                    <tr
                      key={member.id}
                      className={member.id === selectedId ? 'is-selected' : undefined}
                      onClick={() => setSelectedId(member.id)}
                    >
                      <td>
                        <b>{memberName(member)}</b>
                        <span>{contactLabel(member)}</span>
                      </td>
                      <td>
                        <span className={`aud-verdict aud-verdict--${member.verdict}`}>
                          {member.scored_at === null ? 'Не оценён' : VERDICT_LABEL[member.verdict]}
                        </span>
                      </td>
                        <td className="aud-num">{member.scored_at === null ? '—' : member.overall_score}</td>
                      <td className="aud-num">{member.messages_count}</td>
                      <td>{formatDate(member.last_message_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(scores?.total ?? 0) > (scores?.items.length ?? 0) && (
              <div
                style={{
                  padding: '10px 14px',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11.5,
                  color: 'var(--muted)',
                  borderTop: '1px solid var(--line)',
                }}
              >
                Показаны {scores?.items.length} из {scores?.total} участников · полный список в CSV
              </div>
            )}
          </div>

          <div
            className="aud-grid__resizer"
            onPointerDown={startResize}
            role="separator"
            aria-orientation="vertical"
            aria-label="Изменить ширину областей аудитории"
            title="Потяните, чтобы изменить ширину"
          />
          <AudienceDetail key={selected?.id ?? 'empty'} member={selected} />
        </div>
      )}
    </section>
  )
}
